import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Alert,
  CircularProgress,
  Paper,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Divider,
  Stack,
  Badge,
  TextField,
  Tabs,
  Tab,
  InputAdornment,
  TextField as MuiTextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Assessment as ReportsIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  ShoppingCart as OrdersIcon,
  AttachMoney as RevenueIcon,
  People as CustomersIcon,
  Inventory as ProductsIcon,
  GetApp as ExportIcon,
  AccountBalance as AccountBalanceIcon,
  ShowChart as ShowChartIcon,
  LocalShipping as ShippingIcon,
  Receipt as ReceiptIcon,
  Search as SearchIcon,
  CheckCircle as DeliveredIcon,
  Cancel as CancelledIcon,
  Dashboard as DashboardIcon,
  TableChart as TableChartIcon,
  ContentCopy as ContentCopyIcon
} from '@mui/icons-material';
import adminAuthService from '../services/adminAuthService';
import { API_BASE_URL } from '../config/api';
import * as XLSX from 'xlsx';
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Line
} from 'recharts';

const AdminReports = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reportsData, setReportsData] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('last_month');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [isPasswordVerified, setIsPasswordVerified] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [activeTab, setActiveTab] = useState(0);
  const [productSearch, setProductSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortBy, setSortBy] = useState('delivered_quantity'); // revenue, delivered_quantity, cancelled_quantity, total_quantity
  const [sortOrder, setSortOrder] = useState('desc'); // asc or desc
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const today = new Date().toISOString().split('T')[0];
  const [selectedDateStart, setSelectedDateStart] = useState(today);
  const [selectedDateEnd, setSelectedDateEnd] = useState(today);
  const [dailyProducts, setDailyProducts] = useState([]);
  const [loadingDaily, setLoadingDaily] = useState(false);
  const [dailyError, setDailyError] = useState('');
  const [dailyProductSearch, setDailyProductSearch] = useState('');
  const [dailyBrandFilter, setDailyBrandFilter] = useState('');
  const [dailyCategoryFilter, setDailyCategoryFilter] = useState('');
  const [dailyPage, setDailyPage] = useState(0);
  const [dailyRowsPerPage, setDailyRowsPerPage] = useState(100);
  const navigate = useNavigate();

  // Orders list dialog state
  const [ordersDialogOpen, setOrdersDialogOpen] = useState(false);
  const [ordersDialogStatus, setOrdersDialogStatus] = useState(''); // 'delivered' or 'cancelled'
  const [ordersList, setOrdersList] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [ordersPage, setOrdersPage] = useState(0);
  const [ordersRowsPerPage, setOrdersRowsPerPage] = useState(10);

  const REPORTS_PASSWORD = 'mhh9971';

  const monthNames = [
    'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
    'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'
  ];

  const COLORS = ['#4caf50', '#2196f3', '#ff9800', '#f44336', '#9c27b0', '#00bcd4', '#8bc34a', '#ff5722'];

  // Detect mobile device
  const isMobile = window.innerWidth < 768;

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography key={index} variant="caption" sx={{ color: entry.color, display: 'block' }}>
              {entry.name}: {entry.name.includes('دينار') || entry.name.includes('إيراد') || entry.name.includes('تكلفة') 
                ? formatCurrency(entry.value) 
                : formatNumber(entry.value)}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  useEffect(() => {
    if (!adminAuthService.isAuthenticated() || !adminAuthService.isAdmin()) {
      navigate('/admin/login');
      return;
    }
    
    // Check if password is already verified in session
    const passwordVerified = sessionStorage.getItem('reportsPasswordVerified');
    if (passwordVerified === 'true') {
      setIsPasswordVerified(true);
    } else {
      setLoading(false); // Stop loading to show password entry form
    }
  }, [navigate]);

  useEffect(() => {
    if (isPasswordVerified) {
      loadReportsData();
    }
  }, [isPasswordVerified, selectedPeriod, selectedYear, selectedMonth]);

  useEffect(() => {
    if (isPasswordVerified && activeTab === 2 && selectedDateStart && selectedDateEnd) {
      loadDailyProducts(selectedDateStart, selectedDateEnd);
    }
  }, [isPasswordVerified, activeTab, selectedDateStart, selectedDateEnd]);

  const loadDailyProducts = async (dateStart, dateEnd) => {
    setLoadingDaily(true);
    setDailyError('');
    try {
      const token = adminAuthService.getToken();
      const params = new URLSearchParams();
      if (dateStart === dateEnd) {
        params.set('date', dateStart);
      } else {
        params.set('date_start', dateStart);
        params.set('date_end', dateEnd);
      }
      const response = await fetch(
        `${API_BASE_URL}/api/admin_reports.php?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Response not OK:', response.status, errorText);
        throw new Error(`فشل في جلب بيانات المبيعات اليومية (${response.status})`);
      }

      const data = await response.json();
      console.log('Daily products response:', data);
      if (data.success && data.daily_products) {
        setDailyProducts(data.daily_products || []);
      } else {
        throw new Error(data.message || 'فشل في جلب البيانات');
      }
    } catch (err) {
      console.error('Error loading daily products:', err);
      setDailyError(err.message);
      setDailyProducts([]);
    } finally {
      setLoadingDaily(false);
    }
  };

  const loadReportsData = async () => {
    try {
      setLoading(true);
      setError('');

      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_reports.php?period=${selectedPeriod}&year=${selectedYear}&month=${selectedMonth}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('فشل في تحميل التقارير');
      }

      const data = await response.json();
      console.log('Reports API response:', data); // Debug log
      if (data.success) {
        console.log('Reports data:', data.data); // Debug log
        console.log('Revenue trend:', data.data?.revenue_trend);
        console.log('Top products:', data.data?.top_products);
        console.log('Summary:', data.data?.summary);
        setReportsData(data.data);
      } else {
        throw new Error(data.message || 'فشل في تحميل التقارير');
      }
    } catch (error) {
      console.error('Error loading reports:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadOrdersByStatus = async (status) => {
    try {
      setLoadingOrders(true);
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_reports.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'get_sold_items',
          period: selectedPeriod,
          year: selectedYear,
          month: selectedMonth
        })
      });

      if (!response.ok) {
        throw new Error('فشل في تحميل الطلبات');
      }

      const data = await response.json();
      if (data.success && data.items) {
        // Extract unique orders and filter by status
        const ordersMap = new Map();
        
        data.items.forEach(item => {
          if (item.order_status === status) {
            const orderId = item.order_id;
            if (!ordersMap.has(orderId)) {
              ordersMap.set(orderId, {
                id: orderId,
                customer_name: item.customer_name || 'غير محدد',
                customer_phone: item.customer_phone || '-',
                created_at: item.order_date,
                total_amount: 0,
                items_count: 0
              });
            }
            
            const order = ordersMap.get(orderId);
            order.total_amount += (item.sell_price || 0) * (item.quantity || 0);
            order.items_count += item.quantity || 0;
          }
        });
        
        const orders = Array.from(ordersMap.values());
        // Sort by order ID descending
        orders.sort((a, b) => b.id - a.id);
        setOrdersList(orders);
      } else {
        throw new Error(data.message || 'فشل في تحميل الطلبات');
      }
    } catch (error) {
      console.error('Error loading orders:', error);
      setError(error.message);
      setOrdersList([]);
    } finally {
      setLoadingOrders(false);
    }
  };

  const handleShowOrders = (status) => {
    setOrdersDialogStatus(status);
    setOrdersDialogOpen(true);
    setOrdersPage(0);
    loadOrdersByStatus(status);
  };

  const handleExportOrderIds = () => {
    if (!ordersList || ordersList.length === 0) {
      alert('لا توجد طلبات للتصدير');
      return;
    }

    // Extract order IDs and join with comma
    const orderIds = ordersList.map(order => order.id).join(', ');

    // Copy to clipboard
    navigator.clipboard.writeText(orderIds).then(() => {
      alert(`تم نسخ ${ordersList.length} رقم طلب إلى الحافظة`);
    }).catch(err => {
      console.error('Failed to copy:', err);
      // Fallback: download as text file
      const blob = new Blob([orderIds], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `order_ids_${ordersDialogStatus}_${new Date().toISOString().split('T')[0]}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    });
  };

  const handleExportReport = async () => {
    try {
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_reports.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'export',
          period: selectedPeriod,
          year: selectedYear,
          month: selectedMonth
        })
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `تقرير_${selectedYear}_${selectedMonth}.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error exporting report:', error);
      setError('فشل في تصدير التقرير');
    }
  };

  const handleExportSoldItems = async () => {
    try {
      setLoading(true);
      const token = adminAuthService.getToken();
      const response = await fetch(`${API_BASE_URL}/api/admin_reports.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          action: 'get_sold_items',
          period: selectedPeriod,
          year: selectedYear,
          month: selectedMonth
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch sold items');
      }

      const data = await response.json();
      
      if (!data.success || !data.items) {
        throw new Error(data.message || 'No data available');
      }

      // Create workbook with only delivered orders
      const wb = XLSX.utils.book_new();

      // Delivered Orders Only
      const deliveredItems = data.items.filter(item => item.order_status === 'delivered');
      const deliveredData = deliveredItems.map(item => ({
        'SKU': item.sku || '',
        'اسم المنتج': item.product_name || '',
        'الكمية المباعة': item.quantity || 0,
        'سعر الشراء (للوحدة)': item.purchase_price || 0,
        'سعر البيع (للوحدة)': item.sell_price || 0,
        'إجمالي سعر الشراء': (item.purchase_price || 0) * (item.quantity || 0),
        'إجمالي سعر البيع': (item.sell_price || 0) * (item.quantity || 0),
        'الربح': ((item.sell_price || 0) - (item.purchase_price || 0)) * (item.quantity || 0),
        'رقم الطلب': item.order_id || '',
        'تاريخ الطلب': item.order_date || ''
      }));

      const wsDelivered = XLSX.utils.json_to_sheet(deliveredData);
      XLSX.utils.book_append_sheet(wb, wsDelivered, 'الطلبات المسلمة');

      // Generate filename
      const periodName = selectedPeriod === 'current_month' ? 'الشهر_الحالي' :
                        selectedPeriod === 'last_month' ? 'الشهر_الماضي' :
                        selectedPeriod === 'current_year' ? 'السنة_الحالية' :
                        `${selectedYear}_${selectedMonth}`;
      
      const filename = `المنتجات_المباعة_${periodName}.xlsx`;

      // Download
      XLSX.writeFile(wb, filename);
      
    } catch (error) {
      console.error('Error exporting sold items:', error);
      setError('فشل في تصدير المنتجات المباعة: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('ar-IQ', {
      style: 'currency',
      currency: 'IQD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('ar-IQ').format(num || 0);
  };

  const formatPercentage = (value) => {
    return `${(value || 0).toFixed(1)}%`;
  };

  const getFilteredDailyProducts = () => {
    if (!dailyProducts || dailyProducts.length === 0) {
      return [];
    }
    
    let filtered = [...dailyProducts];
    
    // Apply search filter
    if (dailyProductSearch.trim()) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(dailyProductSearch.toLowerCase())
      );
    }

    // Apply brand filter
    if (dailyBrandFilter) {
      filtered = filtered.filter(product => 
        product.brand_name === dailyBrandFilter
      );
    }

    // Apply category filter
    if (dailyCategoryFilter) {
      filtered = filtered.filter(product => 
        product.category_name === dailyCategoryFilter
      );
    }
    
    return filtered;
  };

  // Get unique brands from daily products
  const getUniqueDailyBrands = () => {
    if (!dailyProducts || !dailyProducts.length) return [];
    const brands = [...new Set(dailyProducts
      .map(p => p.brand_name)
      .filter(b => b && b.trim() !== '')
    )];
    return brands.sort();
  };

  // Get unique categories from daily products
  const getUniqueDailyCategories = () => {
    if (!dailyProducts || !dailyProducts.length) return [];
    const categories = [...new Set(dailyProducts
      .map(p => p.category_name)
      .filter(c => c && c.trim() !== '')
    )];
    return categories.sort();
  };

  const getFilteredProducts = () => {
    if (!reportsData || !reportsData.top_products) {
      console.log('No reportsData or top_products');
      return [];
    }
    
    console.log('Raw top_products data:', reportsData.top_products);
    console.log('First product sample:', reportsData.top_products[0]);
    
    let filtered = [...reportsData.top_products];
    
    // Apply search filter
    if (productSearch.trim()) {
      filtered = filtered.filter(product => 
        product.name.toLowerCase().includes(productSearch.toLowerCase())
      );
    }
    
    // Apply brand filter
    if (brandFilter) {
      filtered = filtered.filter(product => 
        product.brand_name === brandFilter
      );
    }
    
    // Apply category filter
    if (categoryFilter) {
      filtered = filtered.filter(product => 
        product.category_name === categoryFilter
      );
    }
    
    console.log('Filtered products count:', filtered.length);
    if (filtered.length > 0) {
      console.log('First filtered product:', filtered[0]);
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case 'delivered_quantity':
          aVal = a.delivered_quantity || 0;
          bVal = b.delivered_quantity || 0;
          break;
        case 'cancelled_quantity':
          aVal = a.cancelled_quantity || 0;
          bVal = b.cancelled_quantity || 0;
          break;
        case 'total_quantity':
          aVal = (a.delivered_quantity || 0) + (a.cancelled_quantity || 0);
          bVal = (b.delivered_quantity || 0) + (b.cancelled_quantity || 0);
          break;
        case 'revenue':
          aVal = a.delivered_revenue || 0;
          bVal = b.delivered_revenue || 0;
          break;
        case 'profit':
          aVal = (a.delivered_revenue || 0) - ((a.purchase_price || 0) * (a.delivered_quantity || 0));
          bVal = (b.delivered_revenue || 0) - ((b.purchase_price || 0) * (b.delivered_quantity || 0));
          break;
        default:
          aVal = a.delivered_quantity || 0;
          bVal = b.delivered_quantity || 0;
      }
      
      return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
    });
    
    return filtered;
  };

  // Get unique brands from products
  const getUniqueBrands = () => {
    if (!reportsData || !reportsData.top_products) return [];
    const brands = [...new Set(reportsData.top_products
      .map(p => p.brand_name)
      .filter(b => b && b.trim() !== '')
    )];
    return brands.sort();
  };

  // Get unique categories from products
  const getUniqueCategories = () => {
    if (!reportsData || !reportsData.top_products) return [];
    const categories = [...new Set(reportsData.top_products
      .map(p => p.category_name)
      .filter(c => c && c.trim() !== '')
    )];
    return categories.sort();
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === REPORTS_PASSWORD) {
      setIsPasswordVerified(true);
      sessionStorage.setItem('reportsPasswordVerified', 'true');
      setPasswordError('');
      setPasswordInput('');
    } else {
      setPasswordError('كلمة المرور غير صحيحة');
      setPasswordInput('');
    }
  };

  const getTrendIcon = (value) => {
    if (value > 0) {
      return <TrendingUpIcon sx={{ fontSize: 18, color: '#4caf50' }} />;
    } else if (value < 0) {
      return <TrendingDownIcon sx={{ fontSize: 18, color: '#f44336' }} />;
    }
    return null;
  };

  const MetricCard = ({ title, value, subtitle, icon, color, trend, trendValue }) => (
    <Card sx={{ height: '100%', borderLeft: `4px solid ${color}` }}>
      <CardContent>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 500 }}>
              {title}
            </Typography>
            <Box sx={{ 
              bgcolor: `${color}15`, 
              borderRadius: 1, 
              p: 0.75,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              {React.cloneElement(icon, { sx: { color, fontSize: 20 } })}
            </Box>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#1a237e' }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="text.secondary">
              {subtitle}
            </Typography>
          )}
          {trend !== undefined && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {getTrendIcon(trendValue || 0)}
              <Typography 
                variant="caption" 
                sx={{ 
                  color: (trendValue || 0) >= 0 ? '#4caf50' : '#f44336',
                  fontWeight: 600
                }}
              >
                {formatPercentage(Math.abs(trendValue || 0))}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                مقارنة بالفترة السابقة
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Password Protection Dialog
  if (!isPasswordVerified) {
    return (
      <Box sx={{ flexGrow: 1, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
        <AppBar position="static" sx={{ bgcolor: '#1a237e' }}>
          <Toolbar>
            <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')}>
              <BackIcon />
            </IconButton>
            <ReportsIcon sx={{ mr: 2 }} />
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
              التقارير المالية والإحصائية
            </Typography>
          </Toolbar>
        </AppBar>

        <Container maxWidth="sm" sx={{ mt: { xs: 4, md: 8 }, px: { xs: 2, sm: 3 } }}>
          <Paper 
            elevation={3} 
            sx={{ 
              p: { xs: 2, sm: 3, md: 4 }, 
              borderRadius: 2,
              textAlign: 'center'
            }}
          >
            <Box
              sx={{
                width: { xs: 60, sm: 80 },
                height: { xs: 60, sm: 80 },
                borderRadius: '50%',
                bgcolor: '#1a237e',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: { xs: '0 auto 16px', sm: '0 auto 24px' }
              }}
            >
              <ReportsIcon sx={{ fontSize: { xs: 30, sm: 40 }, color: 'white' }} />
            </Box>
            
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: '#1a237e' }}>
              صفحة محمية
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              يرجى إدخال كلمة المرور للوصول إلى التقارير المالية
            </Typography>

            <form onSubmit={handlePasswordSubmit}>
              <TextField
                fullWidth
                type="password"
                label="كلمة المرور"
                value={passwordInput}
                onChange={(e) => setPasswordInput(e.target.value)}
                error={!!passwordError}
                helperText={passwordError}
                autoFocus
                sx={{ mb: 3 }}
              />
              
              <Button
                fullWidth
                variant="contained"
                type="submit"
                size="large"
                sx={{ 
                  bgcolor: '#1a237e',
                  '&:hover': { bgcolor: '#0d47a1' },
                  py: 1.5
                }}
              >
                دخول
              </Button>
            </form>
          </Paper>
        </Container>
      </Box>
    );
  }

  // Calculate cancelled orders totals from product data
  const getCancelledOrdersTotals = () => {
    if (!reportsData || !reportsData.top_products) {
      return {
        cancelledPurchaseTotal: 0,
        cancelledSellTotal: 0
      };
    }

    let cancelledPurchaseTotal = 0;
    let cancelledSellTotal = 0;

    reportsData.top_products.forEach(product => {
      const cancelledQty = product.cancelled_quantity || 0;
      const purchasePrice = product.purchase_price || 0;
      const sellPrice = product.sell_price || 0;

      cancelledPurchaseTotal += cancelledQty * purchasePrice;
      cancelledSellTotal += cancelledQty * sellPrice;
    });

    return {
      cancelledPurchaseTotal,
      cancelledSellTotal
    };
  };

  const cancelledTotals = getCancelledOrdersTotals();

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header */}
      <AppBar position="static" sx={{ bgcolor: '#1a237e' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={() => navigate('/admin/dashboard')}>
            <BackIcon />
          </IconButton>
          <ReportsIcon sx={{ mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1, fontWeight: 600 }}>
            التقارير المالية والإحصائية
          </Typography>
          <Button
            color="inherit"
            variant="outlined"
            startIcon={<TableChartIcon />}
            onClick={handleExportSoldItems}
            disabled={loading}
            sx={{ 
              mr: 1,
              borderColor: 'white',
              '&:hover': { 
                borderColor: 'white', 
                bgcolor: 'rgba(255,255,255,0.1)' 
              }
            }}
          >
            تصدير المنتجات
          </Button>
          <Button
            color="inherit"
            variant="outlined"
            startIcon={<ExportIcon />}
            onClick={handleExportReport}
            sx={{ 
              borderColor: 'white',
              '&:hover': { 
                borderColor: 'white', 
                bgcolor: 'rgba(255,255,255,0.1)' 
              }
            }}
          >
            تصدير التقرير
          </Button>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: { xs: 2, md: 4 }, px: { xs: 1, sm: 2, md: 3 } }}>
        {/* Filter Controls */}
        <Paper 
          elevation={0} 
          sx={{ 
            p: { xs: 2, md: 3 }, 
            mb: { xs: 2, md: 3 }, 
            border: '1px solid #e0e0e0',
            borderRadius: 2
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
            <ShowChartIcon sx={{ color: '#1a237e' }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
              تحديد الفترة الزمنية
            </Typography>
          </Stack>
          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>الفترة الزمنية</InputLabel>
                <Select
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  label="الفترة الزمنية"
                >
                  <MenuItem value="current_month">الشهر الحالي</MenuItem>
                  <MenuItem value="last_month">الشهر الماضي</MenuItem>
                  <MenuItem value="current_year">السنة الحالية</MenuItem>
                  <MenuItem value="custom">فترة مخصصة</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {selectedPeriod === 'custom' && (
              <>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>السنة</InputLabel>
                    <Select
                      value={selectedYear}
                      onChange={(e) => setSelectedYear(e.target.value)}
                      label="السنة"
                    >
                      {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i).map(year => (
                        <MenuItem key={year} value={year}>{year}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl fullWidth size="small">
                    <InputLabel>الشهر</InputLabel>
                    <Select
                      value={selectedMonth}
                      onChange={(e) => setSelectedMonth(e.target.value)}
                      label="الشهر"
                    >
                      {monthNames.map((month, index) => (
                        <MenuItem key={index + 1} value={index + 1}>{month}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </>
            )}
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {reportsData && (
          <>
            {/* Tabs Navigation */}
            <Paper 
              elevation={0} 
              sx={{ 
                mb: 3, 
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                overflow: 'hidden'
              }}
            >
              <Tabs 
                value={activeTab} 
                onChange={(e, newValue) => setActiveTab(newValue)}
                sx={{
                  bgcolor: '#f8fafc',
                  '& .MuiTab-root': {
                    fontWeight: 600,
                    fontSize: '1rem',
                    minHeight: 64
                  }
                }}
                variant="fullWidth"
              >
                <Tab 
                  icon={<DashboardIcon />} 
                  label="نظرة عامة" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<TableChartIcon />} 
                  label="تحليل المنتجات التفصيلي" 
                  iconPosition="start"
                />
                <Tab 
                  icon={<SearchIcon />} 
                  label="مبيعات يوم محدد" 
                  iconPosition="start"
                />
              </Tabs>
            </Paper>

            {/* Tab Content */}
            {activeTab === 0 && (
          <>
            {/* Financial Overview Section */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: { xs: 2, md: 3 }, 
                mb: { xs: 2, md: 3 }, 
                border: '1px solid #e0e0e0',
                borderRadius: 2,
                bgcolor: '#ffffff'
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: { xs: 2, md: 3 }, flexWrap: 'wrap' }}>
                <AccountBalanceIcon sx={{ color: '#1a237e', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  الملخص المالي
                </Typography>
              </Stack>

              {/* Primary Financial Metrics */}
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="إجمالي الإيرادات"
                    value={formatCurrency(reportsData.summary?.sell_price_total || 0)}
                    subtitle="إجمالي مبيعات الفترة"
                    icon={<RevenueIcon />}
                    color="#4caf50"
                    trend={true}
                    trendValue={reportsData.summary?.revenue_change}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="تكلفة المبيعات"
                    value={formatCurrency(reportsData.summary?.purchase_price_total || 0)}
                    subtitle="إجمالي تكلفة البضاعة المباعة"
                    icon={<ShippingIcon />}
                    color="#f44336"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="صافي الربح"
                    value={formatCurrency((reportsData.summary?.sell_price_total || 0) - (reportsData.summary?.purchase_price_total || 0))}
                    subtitle="الربح بعد خصم التكاليف"
                    icon={<RevenueIcon />}
                    color="#ff9800"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="هامش الربح الإجمالي"
                    value={formatPercentage(
                      reportsData.summary?.sell_price_total && reportsData.summary?.purchase_price_total 
                        ? (((reportsData.summary.sell_price_total - reportsData.summary.purchase_price_total) / reportsData.summary.sell_price_total) * 100)
                        : 0
                    )}
                    subtitle="نسبة الربح من المبيعات"
                    icon={<ShowChartIcon />}
                    color="#9c27b0"
                  />
                </Grid>
              </Grid>

              {/* Delivered Orders Summary */}
              <Divider sx={{ my: 3 }} />
              
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <DeliveredIcon sx={{ color: '#4caf50', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#4caf50' }}>
                  ملخص الطلبات المسلمة
                </Typography>
              </Stack>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%', borderLeft: '4px solid #4caf50', bgcolor: '#f1f8e9' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        إجمالي سعر الشراء
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                        {formatCurrency(reportsData.summary?.purchase_price_total || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        تكلفة المنتجات المباعة
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%', borderLeft: '4px solid #4caf50', bgcolor: '#f1f8e9' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        إجمالي سعر البيع
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                        {formatCurrency(reportsData.summary?.sell_price_total || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        إيرادات المبيعات
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%', borderLeft: '4px solid #4caf50', bgcolor: '#f1f8e9' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        صافي الربح
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                        {formatCurrency((reportsData.summary?.sell_price_total || 0) - (reportsData.summary?.purchase_price_total || 0))}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        الربح من الطلبات المسلمة
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #4caf50', 
                      bgcolor: '#f1f8e9',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)'
                      }
                    }}
                    onClick={() => handleShowOrders('delivered')}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        عدد الطلبات
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#4caf50' }}>
                        {formatNumber(reportsData.summary?.delivered_orders || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        طلب مسلم بنجاح
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#4caf50', fontWeight: 600 }}>
                        اضغط لعرض القائمة
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Cancelled Orders Summary */}
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <CancelledIcon sx={{ color: '#f44336', fontSize: 28 }} />
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#f44336' }}>
                  ملخص الطلبات الملغاة
                </Typography>
              </Stack>
              
              <Grid container spacing={3} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%', borderLeft: '4px solid #f44336', bgcolor: '#ffebee' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        خسارة سعر الشراء
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#f44336' }}>
                        {formatCurrency(cancelledTotals.cancelledPurchaseTotal)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        تكلفة المنتجات الملغاة
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%', borderLeft: '4px solid #f44336', bgcolor: '#ffebee' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        خسارة سعر البيع
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#f44336' }}>
                        {formatCurrency(cancelledTotals.cancelledSellTotal)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        إيرادات ضائعة
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card sx={{ height: '100%', borderLeft: '4px solid #f44336', bgcolor: '#ffebee' }}>
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        خسارة الربح المتوقع
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#f44336' }}>
                        {formatCurrency(cancelledTotals.cancelledSellTotal - cancelledTotals.cancelledPurchaseTotal)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        الربح الضائع من الإلغاءات
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} sm={6} md={3}>
                  <Card 
                    sx={{ 
                      height: '100%', 
                      borderLeft: '4px solid #f44336', 
                      bgcolor: '#ffebee',
                      cursor: 'pointer',
                      transition: 'transform 0.2s, box-shadow 0.2s',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)'
                      }
                    }}
                    onClick={() => handleShowOrders('cancelled')}
                  >
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
                        عدد الطلبات
                      </Typography>
                      <Typography variant="h5" sx={{ fontWeight: 700, color: '#f44336' }}>
                        {formatNumber(reportsData.summary?.cancelled_orders || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        طلب ملغي
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', mt: 1, color: '#f44336', fontWeight: 600 }}>
                        اضغط لعرض القائمة
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Detailed Financial Statement */}
              <Divider sx={{ my: 3 }} />
              
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1a237e' }}>
                قائمة الدخل التفصيلية
              </Typography>
              
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '1rem' }}>الإيرادات</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '1rem' }}></TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, fontSize: '1rem' }}></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>إجمالي المبيعات (الطلبات المسلمة)</TableCell>
                      <TableCell align="right">{formatCurrency(reportsData.summary?.sell_price_total || 0)}</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>عدد الوحدات المباعة</TableCell>
                      <TableCell align="right">{formatNumber(reportsData.summary?.items_sold || 0)} وحدة</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>عدد الطلبات المسلمة</TableCell>
                      <TableCell align="right">{formatNumber(reportsData.summary?.delivered_orders || 0)} طلب</TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>عدد الطلبات الملغاة</TableCell>
                      <TableCell align="right" sx={{ color: '#f44336' }}>
                        {formatNumber(reportsData.summary?.cancelled_orders || 0)} طلب
                      </TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>إجمالي عدد الطلبات</TableCell>
                      <TableCell align="right">
                        {formatNumber((reportsData.summary?.delivered_orders || 0) + (reportsData.summary?.cancelled_orders || 0))} طلب
                      </TableCell>
                      <TableCell align="right"></TableCell>
                    </TableRow>
                    
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600, fontSize: '1rem', pt: 3 }}>التكاليف</TableCell>
                      <TableCell align="right" sx={{ pt: 3 }}></TableCell>
                      <TableCell align="right" sx={{ pt: 3 }}></TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell sx={{ pl: 4 }}>تكلفة البضاعة المباعة</TableCell>
                      <TableCell align="right">{formatCurrency(reportsData.summary?.purchase_price_total || 0)}</TableCell>
                      <TableCell align="right" sx={{ color: '#f44336' }}>(-)</TableCell>
                    </TableRow>
                    
                    <TableRow sx={{ 
                      bgcolor: '#e3f2fd', 
                      '& td': { 
                        borderTop: '2px solid #1a237e',
                        fontWeight: 700,
                        fontSize: '1.1rem',
                        py: 2
                      }
                    }}>
                      <TableCell>صافي الدخل (من الطلبات المسلمة فقط)</TableCell>
                      <TableCell align="right" sx={{ color: '#4caf50' }}>
                        {formatCurrency((reportsData.summary?.sell_price_total || 0) - (reportsData.summary?.purchase_price_total || 0))}
                      </TableCell>
                      <TableCell align="right" sx={{ color: '#4caf50' }}>
                        {formatPercentage(
                          reportsData.summary?.sell_price_total 
                            ? (((reportsData.summary.sell_price_total - reportsData.summary.purchase_price_total) / reportsData.summary.sell_price_total) * 100)
                            : 0
                        )}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>

            {/* Operations Metrics */}
            <Paper 
              elevation={0} 
              sx={{ 
                p: 3, 
                mb: 3, 
                border: '1px solid #e0e0e0',
                borderRadius: 2
              }}
            >
              <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                <ReceiptIcon sx={{ color: '#1a237e', fontSize: 28 }} />
                <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>
                  مؤشرات الأداء التشغيلي
                </Typography>
              </Stack>

              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="إجمالي الطلبات"
                    value={formatNumber(reportsData.summary?.total_orders || 0)}
                    subtitle="عدد طلبات الفترة"
                    icon={<OrdersIcon />}
                    color="#2196f3"
                    trend={true}
                    trendValue={reportsData.summary?.orders_change}
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="متوسط قيمة الطلب"
                    value={formatCurrency(reportsData.summary?.avg_order_value || 0)}
                    subtitle="متوسط إنفاق العميل"
                    icon={<ReceiptIcon />}
                    color="#00bcd4"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="عدد العملاء"
                    value={formatNumber(reportsData.summary?.total_customers || 0)}
                    subtitle={`${formatNumber(reportsData.summary?.new_customers || 0)} عميل جديد`}
                    icon={<CustomersIcon />}
                    color="#ff9800"
                  />
                </Grid>

                <Grid item xs={12} sm={6} md={3}>
                  <MetricCard
                    title="المنتجات المباعة"
                    value={formatNumber(reportsData.summary?.items_sold || 0)}
                    subtitle="إجمالي الوحدات"
                    icon={<ProductsIcon />}
                    color="#9c27b0"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Charts Section */}
            <Grid container spacing={3} sx={{ mb: 4 }}>
              {/* Revenue Trend Chart - Visual */}
              <Grid item xs={12} md={8}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <TrendingUpIcon sx={{ color: '#1a237e' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
                        اتجاه الإيرادات والطلبات
                      </Typography>
                    </Stack>
                    
                    {(reportsData.revenue_trend && reportsData.revenue_trend.length > 0) ? (
                      <>
                        <Box sx={{ width: '100%', height: { xs: 250, md: 300 }, mb: { xs: 2, md: 3 } }}>
                          <ResponsiveContainer>
                            <AreaChart
                              data={reportsData.revenue_trend}
                              margin={{ top: 10, right: isMobile ? 10 : 30, left: isMobile ? -20 : 0, bottom: 0 }}
                            >
                              <defs>
                                <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
                                </linearGradient>
                                <linearGradient id="colorOrders" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#2196f3" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#2196f3" stopOpacity={0.1}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis 
                                dataKey="date" 
                                tick={{ fontSize: 12 }}
                                stroke="#666"
                              />
                              <YAxis 
                                yAxisId="left"
                                tick={{ fontSize: 12 }}
                                stroke="#4caf50"
                                label={{ value: 'الإيرادات (د.ع)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                              />
                              <YAxis 
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 12 }}
                                stroke="#2196f3"
                                label={{ value: 'الطلبات', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                              />
                              <Tooltip content={<CustomTooltip />} />
                              <Legend 
                                wrapperStyle={{ fontSize: isMobile ? 11 : 14, fontWeight: 600 }}
                                formatter={(value) => value === 'revenue' ? 'الإيرادات' : 'الطلبات'}
                              />
                              <Area
                                yAxisId="left"
                                type="monotone"
                                dataKey="revenue"
                                name="الإيرادات (د.ع)"
                                stroke="#4caf50"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorRevenue)"
                              />
                              <Line
                                yAxisId="right"
                                type="monotone"
                                dataKey="orders"
                                name="عدد الطلبات"
                                stroke="#2196f3"
                                strokeWidth={2}
                                dot={{ fill: '#2196f3', r: 4 }}
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                      </>
                    ) : null}
                    
                    <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#1a237e', borderRadius: 4 } }}>
                      <Table size="small" sx={{ minWidth: { xs: 600, md: 'auto' } }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>التاريخ</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>الإيرادات (د.ع)</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>عدد الطلبات</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>متوسط الطلب</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(reportsData.revenue_trend && reportsData.revenue_trend.length > 0) ? 
                            reportsData.revenue_trend.map((item, index) => (
                              <TableRow 
                                key={index}
                                sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                              >
                                <TableCell>{item.date}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                  {formatCurrency(item.revenue)}
                                </TableCell>
                                <TableCell align="right">{item.orders}</TableCell>
                                <TableCell align="right">
                                  {item.orders > 0 ? formatCurrency(item.revenue / item.orders) : formatCurrency(0)}
                                </TableCell>
                              </TableRow>
                            )) : (
                              <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                  <Typography color="text.secondary">
                                    لا توجد بيانات إيرادات للفترة المحددة
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            )
                          }
                          {reportsData.revenue_trend && reportsData.revenue_trend.length > 0 && (
                            <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                              <TableCell sx={{ fontWeight: 700 }}>الإجمالي</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                {formatCurrency(reportsData.revenue_trend.reduce((sum, item) => sum + item.revenue, 0))}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {reportsData.revenue_trend.reduce((sum, item) => sum + item.orders, 0)}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatCurrency(
                                  reportsData.revenue_trend.reduce((sum, item) => sum + item.revenue, 0) /
                                  reportsData.revenue_trend.reduce((sum, item) => sum + item.orders, 0)
                                )}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Paper>
              </Grid>

              {/* Order Status Distribution */}
              <Grid item xs={12} md={4}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2,
                    height: '100%'
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <OrdersIcon sx={{ color: '#1a237e' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
                        توزيع حالات الطلبات
                      </Typography>
                    </Stack>
                    
                    {(reportsData.order_status_distribution && reportsData.order_status_distribution.length > 0) ? (
                      <>
                        <Box sx={{ width: '100%', height: { xs: 280, md: 300 }, mb: 2 }}>
                          <ResponsiveContainer>
                            <PieChart>
                              <Pie
                                data={reportsData.order_status_distribution}
                                cx={isMobile ? "45%" : "50%"}
                                cy="50%"
                                labelLine={false}
                                label={false}
                                outerRadius={isMobile ? 70 : 100}
                                innerRadius={isMobile ? 45 : 60}
                                fill="#8884d8"
                                dataKey="count"
                                nameKey="name"
                                paddingAngle={3}
                              >
                                {reportsData.order_status_distribution.map((entry, index) => (
                                  <Cell 
                                    key={`cell-${index}`} 
                                    fill={COLORS[index % COLORS.length]} 
                                  />
                                ))}
                              </Pie>
                              <Tooltip 
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0];
                                    const total = reportsData.order_status_distribution.reduce((sum, item) => sum + item.count, 0);
                                    const percentage = ((data.value / total) * 100).toFixed(1);
                                    return (
                                      <Paper sx={{ p: 2, border: '1px solid #e0e0e0' }}>
                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 1, color: data.payload.fill }}>
                                          {data.name}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                          العدد: {formatNumber(data.value)}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>
                                          النسبة: {percentage}%
                                        </Typography>
                                      </Paper>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Legend 
                                verticalAlign={isMobile ? "bottom" : "middle"}
                                align={isMobile ? "center" : "right"}
                                layout={isMobile ? "horizontal" : "vertical"}
                                iconType="circle"
                                iconSize={isMobile ? 8 : 12}
                                wrapperStyle={{ 
                                  fontSize: isMobile ? 10 : 13, 
                                  paddingLeft: isMobile ? 0 : 20,
                                  direction: 'rtl'
                                }}
                                formatter={(value, entry) => {
                                  const total = reportsData.order_status_distribution.reduce((sum, item) => sum + item.count, 0);
                                  const percentage = ((entry.payload.count / total) * 100).toFixed(0);
                                  return isMobile ? `${percentage}%` : `${value} (${percentage}%)`;
                                }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </Box>
                        
                        <Divider sx={{ my: 2 }} />
                      </>
                    ) : null}
                    
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>الحالة</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>العدد</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>النسبة</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(() => {
                            const total = (reportsData.order_status_distribution || []).reduce((sum, item) => sum + item.count, 0);
                            return (reportsData.order_status_distribution || []).map((item, index) => (
                              <TableRow key={index} sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}>
                                <TableCell>
                                  <Chip 
                                    label={item.name} 
                                    size="small"
                                    sx={{ 
                                      bgcolor: COLORS[index % COLORS.length],
                                      color: 'white',
                                      fontWeight: 600
                                    }}
                                  />
                                </TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{item.count}</TableCell>
                                <TableCell align="right">
                                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1a237e' }}>
                                    {total > 0 ? `${((item.count / total) * 100).toFixed(1)}%` : '0%'}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ));
                          })()}
                          {(!reportsData.order_status_distribution || reportsData.order_status_distribution.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                <Typography color="text.secondary">
                                  لا توجد بيانات متاحة
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Paper>
              </Grid>

              {/* Top Products Table with Profit Analysis */}
              <Grid item xs={12}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                      <Stack direction="row" alignItems="center" spacing={2}>
                        <ProductsIcon sx={{ color: '#1a237e' }} />
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
                          المنتجات المباعة - تحليل الربحية
                        </Typography>
                        <Badge 
                          badgeContent={`${(reportsData.top_products || []).length} منتج`}
                          color="primary"
                        />
                      </Stack>
                      {(!reportsData.top_products || reportsData.top_products.length === 0) && (
                        <Chip 
                          label="لا توجد مبيعات في هذه الفترة" 
                          color="warning"
                          size="small"
                        />
                      )}
                    </Stack>
                    
                    {(reportsData.top_products && reportsData.top_products.length > 0) ? (
                      <>
                        {/* Area Chart - Revenue and Profit Trend */}
                        <Box sx={{ width: '100%', height: { xs: 350, md: 400 }, mb: { xs: 2, md: 3 } }}>
                          <ResponsiveContainer>
                            <AreaChart
                              data={reportsData.top_products.slice(0, isMobile ? 8 : 10).map(item => ({
                                ...item,
                                profit: (item.revenue || 0) - ((item.purchase_price || 0) * (item.quantity || 0)),
                                shortName: item.name.length > 15 ? item.name.substring(0, 15) + '...' : item.name
                              }))}
                              margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? -20 : 0, bottom: isMobile ? 40 : 60 }}
                            >
                              <defs>
                                <linearGradient id="colorProductRevenue" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#4caf50" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#4caf50" stopOpacity={0.1}/>
                                </linearGradient>
                                <linearGradient id="colorProductProfit" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#ff9800" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#ff9800" stopOpacity={0.1}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                              <XAxis 
                                dataKey="shortName"
                                angle={-45}
                                textAnchor="end"
                                height={isMobile ? 60 : 80}
                                tick={{ fontSize: isMobile ? 9 : 11 }}
                                stroke="#666"
                              />
                              <YAxis 
                                yAxisId="left"
                                tick={{ fontSize: 12 }}
                                stroke="#4caf50"
                                label={{ value: 'الإيرادات (د.ع)', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                                tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                              />
                              <YAxis 
                                yAxisId="right"
                                orientation="right"
                                tick={{ fontSize: 12 }}
                                stroke="#ff9800"
                                label={{ value: 'الربح (د.ع)', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                                tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                              />
                              <Tooltip 
                                content={({ active, payload }) => {
                                  if (active && payload && payload.length) {
                                    const data = payload[0].payload;
                                    const profitMargin = data.revenue ? ((data.profit / data.revenue) * 100) : 0;
                                    
                                    return (
                                      <Box sx={{ 
                                        bgcolor: 'rgba(255, 255, 255, 0.98)', 
                                        p: 2, 
                                        border: '1px solid #e0e0e0',
                                        borderRadius: 2,
                                        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                        minWidth: 220
                                      }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: '#1a237e' }}>
                                          {data.name}
                                        </Typography>
                                        <Divider sx={{ mb: 1 }} />
                                        <Stack spacing={0.5}>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">الكمية المباعة:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{formatNumber(data.quantity || 0)}</Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">الإيرادات:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                              {formatCurrency(data.revenue || 0)}
                                            </Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">الربح الصافي:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#ff9800' }}>
                                              {formatCurrency(data.profit)}
                                            </Typography>
                                          </Box>
                                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="caption" color="text.secondary">هامش الربح:</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 600, color: '#9c27b0' }}>
                                              {formatPercentage(profitMargin)}
                                            </Typography>
                                          </Box>
                                        </Stack>
                                      </Box>
                                    );
                                  }
                                  return null;
                                }}
                              />
                              <Legend 
                                wrapperStyle={{ fontSize: isMobile ? 11 : 14, fontWeight: 600 }}
                                formatter={(value) => {
                                  if (value === 'revenue') return 'الإيرادات (د.ع)';
                                  if (value === 'profit') return 'الربح الصافي (د.ع)';
                                  return value;
                                }}
                              />
                              <Area
                                yAxisId="left"
                                type="monotone"
                                dataKey="revenue"
                                name="revenue"
                                stroke="#4caf50"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorProductRevenue)"
                              />
                              <Area
                                yAxisId="right"
                                type="monotone"
                                dataKey="profit"
                                name="profit"
                                stroke="#ff9800"
                                strokeWidth={3}
                                fillOpacity={1}
                                fill="url(#colorProductProfit)"
                              />
                            </AreaChart>
                          </ResponsiveContainer>
                        </Box>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, color: '#1a237e' }}>
                          جدول المنتجات التفصيلي
                        </Typography>
                      </>
                    ) : null}
                    
                    {(reportsData.top_products && reportsData.top_products.length > 0) ? (
                      <TableContainer sx={{ overflowX: 'auto', '&::-webkit-scrollbar': { height: 8 }, '&::-webkit-scrollbar-thumb': { bgcolor: '#1a237e', borderRadius: 4 } }}>
                        <Table size="small" sx={{ minWidth: { xs: 800, md: 'auto' } }}>
                          <TableHead>
                            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                              <TableCell sx={{ fontWeight: 600 }}>المنتج</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>الكمية</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>التكلفة الإجمالية</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>الإيرادات</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>الربح الصافي</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>هامش الربح</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600 }}>ROI</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {reportsData.top_products.map((item, index) => {
                              const purchaseTotal = (item.purchase_price || 0) * (item.quantity || 0);
                              const sellTotal = item.revenue || 0;
                              const profit = sellTotal - purchaseTotal;
                              const profitMargin = sellTotal > 0 ? (profit / sellTotal) * 100 : 0;
                              const roi = purchaseTotal > 0 ? (profit / purchaseTotal) * 100 : 0;
                              
                              return (
                                <TableRow 
                                  key={index}
                                  sx={{ 
                                    '&:hover': { bgcolor: '#f5f5f5' },
                                    bgcolor: index % 2 === 0 ? '#fafafa' : 'white'
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 600 }}>{item.name}</TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label={`${formatNumber(item.quantity || 0)} وحدة`}
                                      size="small"
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: '#f44336' }}>
                                    {formatCurrency(purchaseTotal)}
                                  </TableCell>
                                  <TableCell align="right" sx={{ color: '#4caf50', fontWeight: 600 }}>
                                    {formatCurrency(sellTotal)}
                                  </TableCell>
                                  <TableCell 
                                    align="right" 
                                    sx={{ 
                                      color: profit >= 0 ? '#4caf50' : '#f44336', 
                                      fontWeight: 700,
                                      fontSize: '1rem'
                                    }}
                                  >
                                    {formatCurrency(profit)}
                                  </TableCell>
                                  <TableCell align="right">
                                    <Chip 
                                      label={formatPercentage(profitMargin)}
                                      size="small"
                                      sx={{
                                        bgcolor: profitMargin >= 30 ? '#4caf50' : 
                                                 profitMargin >= 15 ? '#ff9800' : '#f44336',
                                        color: 'white',
                                        fontWeight: 600
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography 
                                      variant="body2"
                                      sx={{ 
                                        color: roi >= 50 ? '#4caf50' : 
                                               roi >= 25 ? '#ff9800' : '#f44336',
                                        fontWeight: 600
                                      }}
                                    >
                                      {formatPercentage(roi)}
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                            <TableRow sx={{ bgcolor: '#e3f2fd' }}>
                              <TableCell sx={{ fontWeight: 700 }}>الإجمالي</TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700 }}>
                                {formatNumber(reportsData.top_products.reduce((sum, item) => sum + (item.quantity || 0), 0))}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#f44336' }}>
                                {formatCurrency(reportsData.top_products.reduce((sum, item) => 
                                  sum + ((item.purchase_price || 0) * (item.quantity || 0)), 0))}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                {formatCurrency(reportsData.top_products.reduce((sum, item) => sum + (item.revenue || 0), 0))}
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                {formatCurrency(
                                  reportsData.top_products.reduce((sum, item) => sum + (item.revenue || 0), 0) -
                                  reportsData.top_products.reduce((sum, item) => sum + ((item.purchase_price || 0) * (item.quantity || 0)), 0)
                                )}
                              </TableCell>
                              <TableCell align="right" colSpan={2}></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    ) : (
                      <Box sx={{ textAlign: 'center', py: 8, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                        <ProductsIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                          لا توجد منتجات مباعة
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          لم يتم بيع أي منتجات في الفترة المحددة ({monthNames[selectedMonth - 1]} {selectedYear})
                        </Typography>
                        <Button 
                          variant="outlined" 
                          sx={{ mt: 2 }}
                          onClick={() => setSelectedPeriod('current_year')}
                        >
                          عرض تقرير السنة الكاملة
                        </Button>
                      </Box>
                    )}
                  </CardContent>
                </Paper>
              </Grid>
            </Grid>

            {/* Top Customers Table */}
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <CustomersIcon sx={{ color: '#1a237e' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
                        أفضل العملاء
                      </Typography>
                      <Badge 
                        badgeContent="TOP 10"
                        color="error"
                      />
                    </Stack>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>اسم العميل</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>الطلبات</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>إجمالي المبلغ</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(reportsData.top_customers || []).map((customer, index) => (
                            <TableRow 
                              key={index}
                              sx={{ 
                                '&:hover': { bgcolor: '#f5f5f5' },
                                bgcolor: index < 3 ? '#fff3e0' : 'white'
                              }}
                            >
                              <TableCell>
                                <Chip 
                                  label={index + 1}
                                  size="small"
                                  sx={{
                                    bgcolor: index === 0 ? '#ffd700' : 
                                             index === 1 ? '#c0c0c0' : 
                                             index === 2 ? '#cd7f32' : '#e0e0e0',
                                    color: index < 3 ? 'white' : 'black',
                                    fontWeight: 700
                                  }}
                                />
                              </TableCell>
                              <TableCell sx={{ fontWeight: 600 }}>{customer.customer_name}</TableCell>
                              <TableCell align="right">
                                <Chip 
                                  label={customer.total_orders}
                                  size="small"
                                  color="primary"
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                {formatCurrency(customer.total_amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!reportsData.top_customers || reportsData.top_customers.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                <Typography color="text.secondary">
                                  لا توجد بيانات متاحة
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Paper>
              </Grid>

              <Grid item xs={12} md={6}>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    border: '1px solid #e0e0e0',
                    borderRadius: 2
                  }}
                >
                  <CardContent>
                    <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                      <ReceiptIcon sx={{ color: '#1a237e' }} />
                      <Typography variant="h6" sx={{ fontWeight: 600, color: '#1a237e' }}>
                        أحدث الطلبات
                      </Typography>
                    </Stack>
                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                            <TableCell sx={{ fontWeight: 600 }}>رقم الطلب</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>العميل</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>الحالة</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>المبلغ</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {(reportsData.recent_orders || []).map((order, index) => (
                            <TableRow 
                              key={index}
                              sx={{ '&:hover': { bgcolor: '#f5f5f5' } }}
                            >
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  #{order.id}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {order.created_at}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2">{order.customer_name}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {order.items_count} منتج
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Chip
                                  label={order.status}
                                  color={
                                    order.status === 'مكتمل' ? 'success' : 
                                    order.status === 'مؤكد' ? 'info' :
                                    order.status === 'قيد الانتظار' ? 'warning' : 
                                    'default'
                                  }
                                  size="small"
                                  sx={{ fontWeight: 600 }}
                                />
                              </TableCell>
                              <TableCell align="right" sx={{ fontWeight: 600, color: '#4caf50' }}>
                                {formatCurrency(order.total_amount)}
                              </TableCell>
                            </TableRow>
                          ))}
                          {(!reportsData.recent_orders || reportsData.recent_orders.length === 0) && (
                            <TableRow>
                              <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                                <Typography color="text.secondary">
                                  لا توجد طلبات حديثة
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </CardContent>
                </Paper>
              </Grid>
            </Grid>
          </>
            )}

            {/* Product Analysis Tab */}
            {activeTab === 1 && (
              <Paper 
                elevation={0} 
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 3
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <ProductsIcon sx={{ color: '#1a237e', fontSize: 32 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>
                    تحليل المنتجات التفصيلي
                  </Typography>
                  <Badge 
                    badgeContent={getFilteredProducts().length}
                    color="primary"
                    sx={{ ml: 2 }}
                  />
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  تحليل شامل لجميع المنتجات مع عرض الكميات المباعة والملغاة حسب حالة الطلب
                </Typography>

                {/* Search and Filter Section */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      placeholder="البحث عن منتج..."
                      value={productSearch}
                      onChange={(e) => {
                        setProductSearch(e.target.value);
                        setPage(0);
                      }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: '#666' }} />
                          </InputAdornment>
                        )
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>العلامة التجارية</InputLabel>
                      <Select
                        value={brandFilter}
                        onChange={(e) => {
                          setBrandFilter(e.target.value);
                          setPage(0);
                        }}
                        label="العلامة التجارية"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="">الكل</MenuItem>
                        {getUniqueBrands().map((brand, index) => (
                          <MenuItem key={index} value={brand}>{brand}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>الفئة</InputLabel>
                      <Select
                        value={categoryFilter}
                        onChange={(e) => {
                          setCategoryFilter(e.target.value);
                          setPage(0);
                        }}
                        label="الفئة"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="">الكل</MenuItem>
                        {getUniqueCategories().map((category, index) => (
                          <MenuItem key={index} value={category}>{category}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControl fullWidth size="small">
                      <InputLabel>ترتيب حسب</InputLabel>
                      <Select
                        value={sortBy}
                        onChange={(e) => {
                          setSortBy(e.target.value);
                          setPage(0);
                        }}
                        label="ترتيب حسب"
                        sx={{ bgcolor: 'white' }}
                      >
                        <MenuItem value="delivered_quantity">الكمية المباعة</MenuItem>
                        <MenuItem value="cancelled_quantity">الكمية الملغاة</MenuItem>
                        <MenuItem value="total_quantity">الكمية الإجمالية</MenuItem>
                        <MenuItem value="revenue">الإيرادات</MenuItem>
                        <MenuItem value="profit">الأرباح</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <Stack direction="row" spacing={1}>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                        sx={{ height: '40px' }}
                      >
                        {sortOrder === 'asc' ? '↑ تصاعدي' : '↓ تنازلي'}
                      </Button>
                      <Button
                        fullWidth
                        variant="outlined"
                        onClick={() => {
                          setProductSearch('');
                          setBrandFilter('');
                          setCategoryFilter('');
                          setPage(0);
                        }}
                        sx={{ height: '40px' }}
                      >
                        إعادة تعيين
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>

                {/* Summary Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <DeliveredIcon sx={{ color: '#4caf50', fontSize: 32 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              إجمالي المباع
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                              {formatNumber(
                                getFilteredProducts().reduce((sum, p) => sum + (p.delivered_quantity || 0), 0)
                              )} وحدة
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#ffebee', borderLeft: '4px solid #f44336' }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <CancelledIcon sx={{ color: '#f44336', fontSize: 32 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              إجمالي الملغي
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#f44336' }}>
                              {formatNumber(
                                getFilteredProducts().reduce((sum, p) => sum + (p.cancelled_quantity || 0), 0)
                              )} وحدة
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <RevenueIcon sx={{ color: '#2196f3', fontSize: 32 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              إيرادات المباع
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196f3' }}>
                              {formatCurrency(
                                getFilteredProducts().reduce((sum, p) => sum + (p.delivered_revenue || 0), 0)
                              )}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>

                  <Grid item xs={12} sm={6} md={3}>
                    <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                      <CardContent>
                        <Stack direction="row" spacing={2} alignItems="center">
                          <ShowChartIcon sx={{ color: '#ff9800', fontSize: 32 }} />
                          <Box>
                            <Typography variant="caption" color="text.secondary">
                              معدل النجاح
                            </Typography>
                            <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                              {(() => {
                                const totalDelivered = getFilteredProducts().reduce((sum, p) => sum + (p.delivered_quantity || 0), 0);
                                const totalCancelled = getFilteredProducts().reduce((sum, p) => sum + (p.cancelled_quantity || 0), 0);
                                const total = totalDelivered + totalCancelled;
                                return total > 0 ? formatPercentage((totalDelivered / total) * 100) : '0%';
                              })()}
                            </Typography>
                          </Box>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>

                {/* Results Info */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    عرض {getFilteredProducts().length > 0 ? page * rowsPerPage + 1 : 0} - {Math.min((page + 1) * rowsPerPage, getFilteredProducts().length)} من {getFilteredProducts().length} منتج
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 120 }}>
                    <InputLabel>عدد الصفوف</InputLabel>
                    <Select
                      value={rowsPerPage}
                      onChange={(e) => {
                        setRowsPerPage(parseInt(e.target.value));
                        setPage(0);
                      }}
                      label="عدد الصفوف"
                      sx={{ bgcolor: 'white' }}
                    >
                      <MenuItem value={25}>25</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                      <MenuItem value={250}>250</MenuItem>
                      <MenuItem value={500}>500</MenuItem>
                      <MenuItem value={getFilteredProducts().length || 1}>الكل ({getFilteredProducts().length})</MenuItem>
                    </Select>
                  </FormControl>
                </Box>

                {/* Detailed Products Table */}
                <TableContainer sx={{ 
                  overflowX: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  '&::-webkit-scrollbar': { height: 8 },
                  '&::-webkit-scrollbar-thumb': { bgcolor: '#1a237e', borderRadius: 4 }
                }}>
                  <Table sx={{ minWidth: 900 }}>
                    <TableHead>
                      <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>اسم المنتج</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>العلامة التجارية</TableCell>
                        <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>الفئة</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#4caf50' }}>
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                            <DeliveredIcon sx={{ fontSize: 18 }} />
                            <span>تم التوصيل</span>
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#f44336' }}>
                          <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                            <CancelledIcon sx={{ fontSize: 18 }} />
                            <span>ملغي</span>
                          </Stack>
                        </TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>إجمالي الطلبات</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>إيرادات المباع</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>الربح الصافي</TableCell>
                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>معدل النجاح</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getFilteredProducts().length > 0 ? (
                        getFilteredProducts()
                          .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                          .map((product, index) => {
                          const actualIndex = page * rowsPerPage + index;
                          const deliveredQty = product.delivered_quantity || 0;
                          const cancelledQty = product.cancelled_quantity || 0;
                          const totalQty = deliveredQty + cancelledQty;
                          const successRate = totalQty > 0 ? (deliveredQty / totalQty) * 100 : 0;
                          const deliveredRevenue = product.delivered_revenue || 0;
                          const deliveredCost = (product.purchase_price || 0) * deliveredQty;
                          const profit = deliveredRevenue - deliveredCost;
                          
                          return (
                            <TableRow 
                              key={index}
                              sx={{ 
                                '&:hover': { bgcolor: '#f8fafc' },
                                bgcolor: actualIndex % 2 === 0 ? '#fafafa' : 'white'
                              }}
                            >
                              <TableCell sx={{ fontWeight: 600 }}>{actualIndex + 1}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {product.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" color="text.secondary">
                                  {product.brand_name || '-'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={product.category_name || 'غير محدد'}
                                  size="small"
                                  variant="outlined"
                                  sx={{ fontSize: '0.75rem' }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`${formatNumber(deliveredQty)} وحدة`}
                                  size="small"
                                  sx={{
                                    bgcolor: '#e8f5e9',
                                    color: '#4caf50',
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={`${formatNumber(cancelledQty)} وحدة`}
                                  size="small"
                                  sx={{
                                    bgcolor: '#ffebee',
                                    color: '#f44336',
                                    fontWeight: 600
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {formatNumber(totalQty)} وحدة
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2" sx={{ fontWeight: 700, color: '#2196f3' }}>
                                  {formatCurrency(deliveredRevenue)}
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 700,
                                    color: profit >= 0 ? '#4caf50' : '#f44336'
                                  }}
                                >
                                  {formatCurrency(profit)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  label={formatPercentage(successRate)}
                                  size="small"
                                  sx={{
                                    bgcolor: successRate >= 75 ? '#4caf50' : 
                                             successRate >= 50 ? '#ff9800' : '#f44336',
                                    color: 'white',
                                    fontWeight: 700
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          );
                        })
                      ) : (
                        <TableRow>
                          <TableCell colSpan={10} align="center" sx={{ py: 8 }}>
                            <ProductsIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                              {productSearch || brandFilter || categoryFilter ? 'لم يتم العثور على منتجات' : 'لا توجد بيانات متاحة'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {productSearch 
                                ? `لم يتم العثور على منتجات تطابق "${productSearch}"`
                                : 'لم يتم بيع أي منتجات في الفترة المحددة'
                              }
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                      
                      {/* Summary Row */}
                      {getFilteredProducts().length > 0 && (
                        <TableRow sx={{ bgcolor: '#e3f2fd', '& td': { fontWeight: 700, fontSize: '1rem' } }}>
                          <TableCell colSpan={4}>الإجمالي</TableCell>
                          <TableCell align="center" sx={{ color: '#4caf50' }}>
                            {formatNumber(
                              getFilteredProducts().reduce((sum, p) => sum + (p.delivered_quantity || 0), 0)
                            )} وحدة
                          </TableCell>
                          <TableCell align="center" sx={{ color: '#f44336' }}>
                            {formatNumber(
                              getFilteredProducts().reduce((sum, p) => sum + (p.cancelled_quantity || 0), 0)
                            )} وحدة
                          </TableCell>
                          <TableCell align="center">
                            {formatNumber(
                              getFilteredProducts().reduce((sum, p) => 
                                sum + (p.delivered_quantity || 0) + (p.cancelled_quantity || 0), 0)
                            )} وحدة
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#2196f3' }}>
                            {formatCurrency(
                              getFilteredProducts().reduce((sum, p) => sum + (p.delivered_revenue || 0), 0)
                            )}
                          </TableCell>
                          <TableCell align="right" sx={{ color: '#4caf50' }}>
                            {formatCurrency(
                              getFilteredProducts().reduce((sum, p) => {
                                const deliveredRevenue = p.delivered_revenue || 0;
                                const deliveredCost = (p.purchase_price || 0) * (p.delivered_quantity || 0);
                                return sum + (deliveredRevenue - deliveredCost);
                              }, 0)
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {(() => {
                              const totalDelivered = getFilteredProducts().reduce((sum, p) => sum + (p.delivered_quantity || 0), 0);
                              const totalCancelled = getFilteredProducts().reduce((sum, p) => sum + (p.cancelled_quantity || 0), 0);
                              const total = totalDelivered + totalCancelled;
                              return total > 0 ? formatPercentage((totalDelivered / total) * 100) : '0%';
                            })()}
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Pagination Controls */}
                {getFilteredProducts().length > 0 && (
                  <TablePagination
                    component="div"
                    count={getFilteredProducts().length}
                    page={page}
                    onPageChange={(event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(event) => {
                      setRowsPerPage(parseInt(event.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[25, 50, 100, 250, 500, { label: 'الكل', value: getFilteredProducts().length || 1 }]}
                    labelRowsPerPage="عدد الصفوف في الصفحة:"
                    labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
                    sx={{
                      borderTop: '1px solid #e0e0e0',
                      mt: 2,
                      '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                        fontSize: '0.875rem'
                      }
                    }}
                  />
                )}

                {/* Export Button */}
                <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    variant="contained"
                    startIcon={<ExportIcon />}
                    onClick={handleExportReport}
                    sx={{ bgcolor: '#1a237e' }}
                  >
                    تصدير التقرير
                  </Button>
                </Box>
              </Paper>
            )}

            {/* Daily Sales Tab */}
            {activeTab === 2 && (
              <Paper 
                elevation={0} 
                sx={{ 
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 3
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
                  <SearchIcon sx={{ color: '#1a237e', fontSize: 32 }} />
                  <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a237e' }}>
                    مبيعات يوم محدد أو نطاق أيام
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  عرض جميع المنتجات المباعة في يوم واحد أو ضمن نطاق زمني مع تفاصيل الكميات والإيرادات
                </Typography>

                {/* Date Selector */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label="من تاريخ"
                      value={selectedDateStart}
                      onChange={(e) => {
                        const nextStart = e.target.value;
                        setSelectedDateStart(nextStart);
                        if (selectedDateEnd < nextStart) {
                          setSelectedDateEnd(nextStart);
                        }
                        setDailyPage(0);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField
                      fullWidth
                      type="date"
                      label="إلى تاريخ"
                      value={selectedDateEnd}
                      onChange={(e) => {
                        const nextEnd = e.target.value;
                        setSelectedDateEnd(nextEnd);
                        if (selectedDateStart > nextEnd) {
                          setSelectedDateStart(nextEnd);
                        }
                        setDailyPage(0);
                      }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: 'white'
                        }
                      }}
                    />
                  </Grid>
                </Grid>

                {dailyError && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {dailyError}
                  </Alert>
                )}

                {loadingDaily ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <>
                    {/* Search */}
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="البحث عن منتج..."
                          value={dailyProductSearch}
                          onChange={(e) => {
                            setDailyProductSearch(e.target.value);
                            setDailyPage(0);
                          }}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <SearchIcon sx={{ color: '#666' }} />
                              </InputAdornment>
                            )
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              bgcolor: 'white'
                            }
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>العلامة التجارية</InputLabel>
                          <Select
                            value={dailyBrandFilter}
                            onChange={(e) => {
                              setDailyBrandFilter(e.target.value);
                              setDailyPage(0);
                            }}
                            label="العلامة التجارية"
                            sx={{ bgcolor: 'white' }}
                          >
                            <MenuItem value="">الكل</MenuItem>
                            {getUniqueDailyBrands().map((brand, index) => (
                              <MenuItem key={index} value={brand}>{brand}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <FormControl fullWidth size="small">
                          <InputLabel>الفئة</InputLabel>
                          <Select
                            value={dailyCategoryFilter}
                            onChange={(e) => {
                              setDailyCategoryFilter(e.target.value);
                              setDailyPage(0);
                            }}
                            label="الفئة"
                            sx={{ bgcolor: 'white' }}
                          >
                            <MenuItem value="">الكل</MenuItem>
                            {getUniqueDailyCategories().map((category, index) => (
                              <MenuItem key={index} value={category}>{category}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      {(dailyProductSearch || dailyBrandFilter || dailyCategoryFilter) && (
                        <Grid item xs={12} md={2}>
                          <Button
                            fullWidth
                            variant="outlined"
                            onClick={() => {
                              setDailyProductSearch('');
                              setDailyBrandFilter('');
                              setDailyCategoryFilter('');
                              setDailyPage(0);
                            }}
                            sx={{ height: '40px' }}
                          >
                            إعادة تعيين
                          </Button>
                        </Grid>
                      )}
                    </Grid>

                    {/* Summary Cards */}
                    {getFilteredDailyProducts().length > 0 && (
                      <Grid container spacing={2} sx={{ mb: 3 }}>
                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ bgcolor: '#e3f2fd', borderLeft: '4px solid #2196f3' }}>
                            <CardContent>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <ProductsIcon sx={{ color: '#2196f3', fontSize: 32 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    إجمالي الطلبات (كل الحالات)
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#2196f3' }}>
                                    {formatNumber(
                                      getFilteredDailyProducts().reduce((sum, p) => sum + (p.total_quantity || 0), 0)
                                    )} وحدة
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ bgcolor: '#e8f5e9', borderLeft: '4px solid #4caf50' }}>
                            <CardContent>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <DeliveredIcon sx={{ color: '#4caf50', fontSize: 32 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    تم التوصيل
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                    {formatNumber(
                                      getFilteredDailyProducts().reduce((sum, p) => sum + (p.delivered_quantity || 0), 0)
                                    )} وحدة
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ bgcolor: '#ffebee', borderLeft: '4px solid #f44336' }}>
                            <CardContent>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <CancelledIcon sx={{ color: '#f44336', fontSize: 32 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    إجمالي الملغي
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#f44336' }}>
                                    {formatNumber(
                                      getFilteredDailyProducts().reduce((sum, p) => sum + (p.cancelled_quantity || 0), 0)
                                    )} وحدة
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ bgcolor: '#fff3e0', borderLeft: '4px solid #ff9800' }}>
                            <CardContent>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <OrdersIcon sx={{ color: '#ff9800', fontSize: 32 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    قيد الانتظار
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#ff9800' }}>
                                    {formatNumber(
                                      getFilteredDailyProducts().reduce((sum, p) => sum + (p.pending_quantity || 0), 0)
                                    )} وحدة
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>

                        <Grid item xs={12} sm={6} md={3}>
                          <Card sx={{ bgcolor: '#f3e5f5', borderLeft: '4px solid #9c27b0' }}>
                            <CardContent>
                              <Stack direction="row" spacing={2} alignItems="center">
                                <RevenueIcon sx={{ color: '#9c27b0', fontSize: 32 }} />
                                <Box>
                                  <Typography variant="caption" color="text.secondary">
                                    إجمالي الإيرادات
                                  </Typography>
                                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                                    {formatCurrency(
                                      getFilteredDailyProducts().reduce((sum, p) => sum + (p.total_revenue || 0), 0)
                                    )}
                                  </Typography>
                                </Box>
                              </Stack>
                            </CardContent>
                          </Card>
                        </Grid>
                      </Grid>
                    )}

                    {/* Results Info */}
                    {getFilteredDailyProducts().length > 0 && (
                      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                          عرض {getFilteredDailyProducts().length > 0 ? dailyPage * dailyRowsPerPage + 1 : 0} - {Math.min((dailyPage + 1) * dailyRowsPerPage, getFilteredDailyProducts().length)} من {getFilteredDailyProducts().length} منتج
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>عدد الصفوف</InputLabel>
                          <Select
                            value={dailyRowsPerPage}
                            onChange={(e) => {
                              setDailyRowsPerPage(parseInt(e.target.value));
                              setDailyPage(0);
                            }}
                            label="عدد الصفوف"
                            sx={{ bgcolor: 'white' }}
                          >
                            <MenuItem value={25}>25</MenuItem>
                            <MenuItem value={50}>50</MenuItem>
                            <MenuItem value={100}>100</MenuItem>
                            <MenuItem value={250}>250</MenuItem>
                            <MenuItem value={500}>500</MenuItem>
                            <MenuItem value={getFilteredDailyProducts().length || 1}>الكل ({getFilteredDailyProducts().length})</MenuItem>
                          </Select>
                        </FormControl>
                      </Box>
                    )}

                    {/* Products Table */}
                    <TableContainer sx={{ 
                      overflowX: 'auto',
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      '&::-webkit-scrollbar': { height: 8 },
                      '&::-webkit-scrollbar-thumb': { bgcolor: '#1a237e', borderRadius: 4 }
                    }}>
                      <Table sx={{ minWidth: 900 }}>
                        <TableHead>
                          <TableRow sx={{ bgcolor: '#f5f7fa' }}>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>#</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>اسم المنتج</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>العلامة التجارية</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: '0.9rem' }}>الفئة</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#2196f3' }}>
                              إجمالي الطلبات
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#ff9800' }}>
                              قيد الانتظار
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#4caf50' }}>
                              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                <DeliveredIcon sx={{ fontSize: 18 }} />
                                <span>تم التوصيل</span>
                              </Stack>
                            </TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem', color: '#f44336' }}>
                              <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
                                <CancelledIcon sx={{ fontSize: 18 }} />
                                <span>ملغي</span>
                              </Stack>
                            </TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>إجمالي الإيرادات</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>إيرادات المباع</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>صافي الأرباح</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>معدل النجاح</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {getFilteredDailyProducts().length > 0 ? (
                            getFilteredDailyProducts()
                              .slice(dailyPage * dailyRowsPerPage, dailyPage * dailyRowsPerPage + dailyRowsPerPage)
                              .map((product, index) => {
                              const actualIndex = dailyPage * dailyRowsPerPage + index;
                              const totalQty = product.total_quantity || 0;
                              const pendingQty = product.pending_quantity || 0;
                              const deliveredQty = product.delivered_quantity || 0;
                              const cancelledQty = product.cancelled_quantity || 0;
                              const successRate = totalQty > 0 ? (deliveredQty / totalQty) * 100 : 0;
                              const totalRevenue = product.total_revenue || 0;
                              const deliveredRevenue = product.delivered_revenue || 0;
                              const totalCost = (product.purchase_price || 0) * totalQty;
                              const profit = totalRevenue - totalCost;
                              
                              return (
                                <TableRow 
                                  key={index}
                                  sx={{ 
                                    '&:hover': { bgcolor: '#f8fafc' },
                                    bgcolor: actualIndex % 2 === 0 ? '#fafafa' : 'white'
                                  }}
                                >
                                  <TableCell sx={{ fontWeight: 600 }}>{actualIndex + 1}</TableCell>
                                  <TableCell>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                      {product.name}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Typography variant="body2" color="text.secondary">
                                      {product.brand_name || '-'}
                                    </Typography>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={product.category_name || 'غير محدد'}
                                      size="small"
                                      variant="outlined"
                                      sx={{ fontSize: '0.75rem' }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={`${formatNumber(totalQty)} وحدة`}
                                      size="small"
                                      sx={{
                                        bgcolor: '#e3f2fd',
                                        color: '#2196f3',
                                        fontWeight: 700
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={`${formatNumber(pendingQty)} وحدة`}
                                      size="small"
                                      sx={{
                                        bgcolor: '#fff3e0',
                                        color: '#ff9800',
                                        fontWeight: 600
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={`${formatNumber(deliveredQty)} وحدة`}
                                      size="small"
                                      sx={{
                                        bgcolor: '#e8f5e9',
                                        color: '#4caf50',
                                        fontWeight: 600
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={`${formatNumber(cancelledQty)} وحدة`}
                                      size="small"
                                      sx={{
                                        bgcolor: '#ffebee',
                                        color: '#f44336',
                                        fontWeight: 600
                                      }}
                                    />
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#9c27b0' }}>
                                      {formatCurrency(totalRevenue)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: '#4caf50' }}>
                                      {formatCurrency(deliveredRevenue)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="right">
                                    <Typography 
                                      variant="body2" 
                                      sx={{ 
                                        fontWeight: 700,
                                        color: profit >= 0 ? '#4caf50' : '#f44336'
                                      }}
                                    >
                                      {formatCurrency(profit)}
                                    </Typography>
                                  </TableCell>
                                  <TableCell align="center">
                                    <Chip
                                      label={formatPercentage(successRate)}
                                      size="small"
                                      sx={{
                                        bgcolor: successRate >= 75 ? '#4caf50' : 
                                                 successRate >= 50 ? '#ff9800' : '#f44336',
                                        color: 'white',
                                        fontWeight: 700
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })
                          ) : (
                            <TableRow>
                              <TableCell colSpan={12} align="center" sx={{ py: 8 }}>
                                <ProductsIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
                                <Typography variant="h6" color="text.secondary" gutterBottom>
                                  {dailyProductSearch ? 'لم يتم العثور على منتجات' : 'لا توجد مبيعات في هذا اليوم'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {dailyProductSearch 
                                    ? `لم يتم العثور على منتجات تطابق "${dailyProductSearch}"`
                                    : selectedDateStart === selectedDateEnd
                                      ? `لم يتم بيع أي منتجات في ${selectedDateStart}`
                                      : `لم يتم بيع أي منتجات بين ${selectedDateStart} و ${selectedDateEnd}`
                                  }
                                </Typography>
                              </TableCell>
                            </TableRow>
                          )}
                          
                          {/* Summary Row */}
                          {getFilteredDailyProducts().length > 0 && (
                            <TableRow sx={{ bgcolor: '#e3f2fd', '& td': { fontWeight: 700, fontSize: '1rem' } }}>
                              <TableCell colSpan={4}>الإجمالي</TableCell>
                              <TableCell align="center" sx={{ color: '#4caf50' }}>
                                {formatNumber(
                                  getFilteredDailyProducts().reduce((sum, p) => sum + (p.delivered_quantity || 0), 0)
                                )} وحدة
                              </TableCell>
                              <TableCell align="center" sx={{ color: '#f44336' }}>
                                {formatNumber(
                                  getFilteredDailyProducts().reduce((sum, p) => sum + (p.cancelled_quantity || 0), 0)
                                )} وحدة
                              </TableCell>
                              <TableCell align="center">
                                {formatNumber(
                                  getFilteredDailyProducts().reduce((sum, p) => 
                                    sum + (p.delivered_quantity || 0) + (p.cancelled_quantity || 0), 0)
                                )} وحدة
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#2196f3' }}>
                                {formatCurrency(
                                  getFilteredDailyProducts().reduce((sum, p) => sum + (p.delivered_revenue || 0), 0)
                                )}
                              </TableCell>
                              <TableCell align="right" sx={{ color: '#4caf50' }}>
                                {formatCurrency(
                                  getFilteredDailyProducts().reduce((sum, p) => {
                                    const deliveredRevenue = p.delivered_revenue || 0;
                                    const deliveredCost = (p.purchase_price || 0) * (p.delivered_quantity || 0);
                                    return sum + (deliveredRevenue - deliveredCost);
                                  }, 0)
                                )}
                              </TableCell>
                              <TableCell align="center">
                                {(() => {
                                  const totalDelivered = getFilteredDailyProducts().reduce((sum, p) => sum + (p.delivered_quantity || 0), 0);
                                  const totalCancelled = getFilteredDailyProducts().reduce((sum, p) => sum + (p.cancelled_quantity || 0), 0);
                                  const total = totalDelivered + totalCancelled;
                                  return total > 0 ? formatPercentage((totalDelivered / total) * 100) : '0%';
                                })()}
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Pagination Controls */}
                    {getFilteredDailyProducts().length > 0 && (
                      <TablePagination
                        component="div"
                        count={getFilteredDailyProducts().length}
                        page={dailyPage}
                        onPageChange={(event, newPage) => setDailyPage(newPage)}
                        rowsPerPage={dailyRowsPerPage}
                        onRowsPerPageChange={(event) => {
                          setDailyRowsPerPage(parseInt(event.target.value, 10));
                          setDailyPage(0);
                        }}
                        rowsPerPageOptions={[25, 50, 100, 250, 500, { label: 'الكل', value: getFilteredDailyProducts().length || 1 }]}
                        labelRowsPerPage="عدد الصفوف في الصفحة:"
                        labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
                        sx={{
                          borderTop: '1px solid #e0e0e0',
                          mt: 2,
                          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                            fontSize: '0.875rem'
                          }
                        }}
                      />
                    )}
                  </>
                )}
              </Paper>
            )}
          </>
        )}
      </Container>

      {/* Orders List Dialog */}
      <Dialog
        open={ordersDialogOpen}
        onClose={() => setOrdersDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: ordersDialogStatus === 'delivered' ? '#4caf50' : '#f44336',
          color: 'white',
          fontWeight: 700
        }}>
          {ordersDialogStatus === 'delivered' ? 'قائمة الطلبات المسلمة' : 'قائمة الطلبات الملغاة'}
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          {loadingOrders ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : ordersList.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">
                لا توجد طلبات في هذه الفترة
              </Typography>
            </Box>
          ) : (
            <>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                      <TableCell sx={{ fontWeight: 600 }}>رقم الطلب</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>اسم العميل</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>رقم الهاتف</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>عدد المنتجات</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>الإجمالي</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>التاريخ</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {ordersList
                      .slice(ordersPage * ordersRowsPerPage, ordersPage * ordersRowsPerPage + ordersRowsPerPage)
                      .map((order) => (
                        <TableRow 
                          key={order.id}
                          sx={{
                            '&:hover': {
                              bgcolor: '#f5f5f5'
                            }
                          }}
                        >
                          <TableCell>#{order.id}</TableCell>
                          <TableCell>{order.customer_name || 'غير محدد'}</TableCell>
                          <TableCell>{order.customer_phone || '-'}</TableCell>
                          <TableCell>{order.items_count || 0}</TableCell>
                          <TableCell>{formatCurrency(order.total_amount || 0)}</TableCell>
                          <TableCell>
                            {order.created_at ? new Date(order.created_at).toLocaleDateString('ar-EG') : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
              
              <TablePagination
                component="div"
                count={ordersList.length}
                page={ordersPage}
                onPageChange={(event, newPage) => setOrdersPage(newPage)}
                rowsPerPage={ordersRowsPerPage}
                onRowsPerPageChange={(event) => {
                  setOrdersRowsPerPage(parseInt(event.target.value, 10));
                  setOrdersPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage="عدد الصفوف:"
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} من ${count}`}
                sx={{ mt: 2, borderTop: '1px solid #e0e0e0' }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button 
            onClick={handleExportOrderIds}
            variant="contained"
            startIcon={<ContentCopyIcon />}
            disabled={ordersList.length === 0}
            sx={{
              bgcolor: ordersDialogStatus === 'delivered' ? '#4caf50' : '#f44336',
              '&:hover': {
                bgcolor: ordersDialogStatus === 'delivered' ? '#45a049' : '#d32f2f'
              }
            }}
          >
            نسخ أرقام الطلبات ({ordersList.length})
          </Button>
          <Button 
            onClick={() => setOrdersDialogOpen(false)}
            variant="outlined"
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminReports;
